import React, { useState, useEffect, useRef } from 'react';
import { useGoogleOAuth } from '@react-oauth/google';
import './GoogleLoginButton.css';

/**
 * GoogleLoginButton
 *
 * Custom DOM-rendered Google Sign-In button.
 *
 * Why we don't use Google's <iframe> renderButton() as our visible button:
 *   - Google renders the button inside a cross-origin iframe with inline
 *     `style="width: 219px"` that we cannot reliably override in some
 *     browser/network contexts (FedCM mode swap causes width flicker).
 *
 * Strategy:
 *   1. Initialize Google Identity Services accounts.id once on mount.
 *   2. Render our OWN visible <button> styled to match Google's brand.
 *   3. On click, call accounts.id.prompt() to trigger Google's One Tap
 *      dialog. The callback receives the ID token, which we forward
 *      to onSuccess.
 *
 * Backend contract is unchanged: api.googleLogin(idToken) still works
 * because we pass the same credential string.
 */
const GoogleLoginButton = ({ onSuccess, disabled = false }) => {
  const [scriptReady, setScriptReady] = useState(false);
  const [isPrompting, setIsPrompting] = useState(false);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const { clientId, scriptLoadedSuccessfully } = useGoogleOAuth();

  // Initialize GIS accounts.id once the script has loaded and we have a client ID.
  useEffect(() => {
    if (!scriptLoadedSuccessfully || !clientId) return;

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: (credentialResponse) => {
        if (onSuccessRef.current && credentialResponse?.credential) {
          onSuccessRef.current(credentialResponse.credential, credentialResponse);
        }
      },
      use_fedcm_for_prompt: false,
      itp_support: false,
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    setScriptReady(true);

    return () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
    };
  }, [clientId, scriptLoadedSuccessfully]);

  const handleClick = () => {
    if (!scriptReady || isPrompting || disabled) return;
    setIsPrompting(true);

    window.google.accounts.id.prompt((notification) => {
      // isNotDisplayed / isSkippedMoment: One Tap couldn't show
      // (e.g., user has dismissed it before, browser doesn't support FedCM).
      // In that case, the user needs to use a different sign-in method.
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        // No fallback popup flow — backend only handles ID tokens,
        // not OAuth codes. The mobile OTP and "Continue without signing in"
        // options remain available.
        console.warn(
          'Google One Tap could not be displayed. Reason:',
          notification.getNotDisplayedReason?.() ||
            notification.getSkippedReason?.(),
        );
      }
      setIsPrompting(false);
    });
  };

  // Disabled state for "not configured"
  if (!clientId) {
    return (
      <button
        type="button"
        className="glb-google-btn glb-google-btn-disabled"
        disabled
        title="Google OAuth not configured. Set REACT_APP_GOOGLE_CLIENT_ID in .env"
      >
        <GoogleLogo />
        <span className="glb-google-btn-text">
          Continue with Google (not configured)
        </span>
      </button>
    );
  }

  const isDisabled = disabled || !scriptReady || isPrompting;

  return (
    <button
      type="button"
      className="glb-google-btn"
      onClick={handleClick}
      disabled={isDisabled}
      aria-label="Continue with Google"
    >
      <GoogleLogo />
      <span className="glb-google-btn-text">
        {!scriptReady
          ? 'Loading…'
          : isPrompting
          ? 'Signing in…'
          : 'Continue with Google'}
      </span>
    </button>
  );
};

// Inline official Google "G" multicolor logo (4-color SVG)
const GoogleLogo = () => (
  <svg
    className="glb-google-btn-logo"
    viewBox="0 0 48 48"
    width="18"
    height="18"
    aria-hidden="true"
  >
    <path
      fill="#4285F4"
      d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
    />
    <path
      fill="#34A853"
      d="M24 46c5.95 0 10.94-1.97 14.59-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.48 2.1-5.75 0-10.62-3.88-12.36-9.1H4.34v5.7C7.96 41.07 15.4 46 24 46z"
    />
    <path
      fill="#FBBC05"
      d="M11.64 28.16c-.44-1.32-.69-2.73-.69-4.16s.25-2.84.69-4.16v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.86l7.3-5.7z"
    />
    <path
      fill="#EA4335"
      d="M24 10.75c3.23 0 6.13 1.11 8.42 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.14l7.3 5.7c1.74-5.22 6.61-9.1 12.36-9.1z"
    />
  </svg>
);

export default GoogleLoginButton;

